// client/src/components/stats/PushFoldBreakdown.tsx
//
// Muestra el push/fold breakdown del hero por posición en short stack (<=15bb).
//
// Tres tipos de columnas:
// 1. Push %      → comparado con Nash range (ok/warn/critical dot)
// 2. Limp %      → cualquier valor > LIMP_LEAK_THRESHOLD es leak (target 0)
// 3. Min-raise % → cualquier valor > MINRAISE_LEAK_THRESHOLD es leak (target 0)
//
// La columna "Sample" muestra n=opp con marca low si está debajo de
// MIN_PUSHFOLD_SAMPLE.

import React from 'react';
import { PushFoldDerived } from '../../utils/pokerStats/types';
import { MIN_PUSHFOLD_SAMPLE } from '../../utils/pokerStats/constants';
import { useT } from '../../lib/i18n';
import { LIMP_LEAK_THRESHOLD, MINRAISE_LEAK_THRESHOLD } from './nashPushFoldBenchmarks';
import { evalBenchmark } from './benchmarks';
import { useBenchmarks } from '../../contexts/BenchmarkContext';

interface PushFoldBreakdownProps {
  pushFold: PushFoldDerived[];
  /** Optional title shown above the table. */
  label?: string;
}

export function PushFoldBreakdown({ pushFold, label }: PushFoldBreakdownProps) {
  const { t } = useT();
  const bm = useBenchmarks();

  const totalOpp = pushFold.reduce((s, p) => s + p.opp, 0);
  const hasAnyData = totalOpp > 0;

  if (!hasAnyData) {
    return (
      <div className="pushfold-breakdown pushfold-empty">
        <p className="pushfold-empty-text">{t('stats.pushFold.empty')}</p>
      </div>
    );
  }

  // At least one position must have sufficient sample to evaluate
  const allLowSample = pushFold.every(p => p.opp === 0 || p.lowSample);

  return (
    <div className="pushfold-breakdown">
      {label && <div className="pushfold-breakdown-title">{label}</div>}

      {allLowSample && (
        <div className="pushfold-warning">
          {t('stats.pushFold.warning', { n: MIN_PUSHFOLD_SAMPLE })}
        </div>
      )}

      <table className="pushfold-table">
        <thead>
          <tr>
            <th>{t('stats.pushFold.thPosition')}</th>
            <th className="pushfold-th-num">{t('stats.pushFold.thPush')}</th>
            <th className="pushfold-th-num">{t('stats.pushFold.thNash')}</th>
            <th className="pushfold-th-num">{t('stats.pushFold.thLimp')}</th>
            <th className="pushfold-th-num">{t('stats.pushFold.thMinRaise')}</th>
            <th className="pushfold-th-num">{t('stats.pushFold.thSample')}</th>
            <th className="pushfold-th-status" />
          </tr>
        </thead>
        <tbody>
          {pushFold.map(p => {
            const benchmark = bm.getPushFoldBenchmark(p.position);
            const canEvaluate = benchmark && !p.lowSample && p.opp > 0;
            const pushStatus = canEvaluate ? evalBenchmark(p.pushPct, benchmark) : 'no-benchmark';

            const limpIsLeak = p.opp > 0 && p.limpPct > LIMP_LEAK_THRESHOLD;
            const minRaiseIsLeak = p.opp > 0 && p.minRaisePct > MINRAISE_LEAK_THRESHOLD;
            const anyLeak = limpIsLeak || minRaiseIsLeak;

            // Row class: prioritize leak markers over benchmark status
            const rowClass = [
              'pushfold-row',
              p.lowSample ? 'pushfold-row-low' : '',
              anyLeak ? 'pushfold-row-leak' : '',
              !anyLeak && pushStatus === 'warn' ? 'pushfold-row-warn' : '',
              !anyLeak && pushStatus === 'critical' ? 'pushfold-row-critical' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <tr key={p.position} className={rowClass}>
                <td className="pushfold-cell-pos">{p.position}</td>
                <td className="pushfold-cell-num pushfold-cell-value">
                  {p.opp > 0 ? `${p.pushPct}%` : '—'}
                </td>
                <td className="pushfold-cell-num pushfold-cell-benchmark">
                  {benchmark ? `${benchmark.min}–${benchmark.max}%` : '—'}
                </td>
                <td
                  className={`pushfold-cell-num ${limpIsLeak ? 'pushfold-cell-leak' : ''}`}
                >
                  {p.opp > 0 ? `${p.limpPct}%` : '—'}
                </td>
                <td
                  className={`pushfold-cell-num ${minRaiseIsLeak ? 'pushfold-cell-leak' : ''}`}
                >
                  {p.opp > 0 ? `${p.minRaisePct}%` : '—'}
                </td>
                <td className="pushfold-cell-num pushfold-cell-sample">
                  {p.opp > 0 ? `n=${p.opp}` : '—'}
                  {p.lowSample && p.opp > 0 && (
                    <span className="pushfold-low-tag"> {t('stats.pushFold.lowTag')}</span>
                  )}
                </td>
                <td className="pushfold-cell-status">
                  {anyLeak && <span className="stat-dot dot-critical" title={t('stats.pushFold.leakTooltip')} />}
                  {!anyLeak && canEvaluate && pushStatus !== 'no-benchmark' && (
                    <span className={`stat-dot dot-${pushStatus}`} />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="pushfold-disclaimer">{t('stats.pushFold.disclaimer')}</p>
    </div>
  );
}
